import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import LinearProgress from '@mui/material/LinearProgress';
import Alert from '@mui/material/Alert';
import { Close } from '@mui/icons-material';
import { listTemplates } from '../../db/sync/templateApi';
import { applyTemplateToCase } from '../../db/actions';

/**
 * Contextual "Apply a template" picker, opened from case setup
 * (pages/Case/EditModal.jsx). Lists the current user's accessible templates
 * (owned + shared) and applies the chosen one to the case via
 * POST /cases/:caseId/apply-template/:templateId. Applying is always an
 * explicit action the user takes — never automatic.
 */
const ApplyTemplateModal = ({ open, onClose, caseId, onApplied }) => {
  const [templates, setTemplates] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [applyingId, setApplyingId] = React.useState(null);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (open) {
      setError(null);
      setLoading(true);
      listTemplates()
        .then((data) => {
          const list = Array.isArray(data) ? data : (data?.templates || []);
          setTemplates(list);
        })
        .catch((err) => {
          console.error('Failed to list templates:', err);
          setError(err);
        })
        .finally(() => setLoading(false));
    }
  }, [open]);

  const handleApply = async (template) => {
    setApplyingId(template._id);
    setError(null);
    try {
      const updatedCase = await applyTemplateToCase(caseId, template._id);
      onApplied?.(updatedCase, template);
      onClose();
    } catch (err) {
      console.error('Failed to apply template:', err);
      setError(err);
    }
    setApplyingId(null);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        Apply a Template
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: 'grey.500' }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {loading && <LinearProgress />}
        {!!error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error.toString().replace('Error: ', '')}
          </Alert>
        )}
        {!loading && templates.length === 0 && !error && (
          <Typography variant="body2" color="text.secondary">
            You don't have any templates yet. Create one from "Manage templates" first.
          </Typography>
        )}
        <List>
          {templates.map((template) => (
            <ListItemButton
              key={template._id}
              disabled={applyingId === template._id}
              onClick={() => handleApply(template)}
            >
              <ListItemText
                primary={template.name}
                secondary={`${(template.categoryTemplates || []).length} categories, ${(template.scoreDefinitions || []).length} scores`}
              />
            </ListItemButton>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApplyTemplateModal;
